import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getDashboardPath = () => {
    if (!user) return "/login";
    if (user.role === "admin") return "/admin/desk";
    if (user.role === "landlord") return "/landlord/dashboard";
    return "/tenant/dashboard";
  };

  const navLinks = [
    { to: "/find", label: "Find a Home" },
    { to: "/let", label: "Let Property" },
    { to: "/move", label: "Move" },
    { to: "/partners", label: "Partners" },
    { to: "/about", label: "About" },
    { to: "/faqs", label: "FAQs" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? "shadow-[0_1px_0_0_#E5EBF5]" : "border-b border-[#E5EBF5]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-brand-logo"
            className="flex items-center space-x-3 group"
          >
            <div className="flex items-baseline space-x-1">
              <span className="text-[22px] tracking-[0.22em] font-heading font-bold text-[#091F5C]">
                FIND
              </span>
              <span className="text-[#7096D1] font-light">·</span>
              <span className="text-[22px] tracking-[0.22em] font-heading font-bold text-[#091F5C]">
                LET
              </span>
            </div>
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.24em] text-[#4A5B78] font-medium border-l border-[#D0E4FE] pl-3 ml-1">
              London Concierge
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace("/", "")}`}
                className={`relative px-4 py-2 text-[13px] font-medium tracking-wide transition-colors ${
                  isActive(link.to)
                    ? "text-[#091F5C]"
                    : "text-[#4A5B78] hover:text-[#091F5C]"
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <span className="absolute left-4 right-4 -bottom-0.5 h-px bg-[#091F5C]" />
                )}
              </Link>
            ))}
          </nav>

          {/* Auth actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <Link
                  to={getDashboardPath()}
                  data-testid="nav-dashboard-btn"
                  className="px-5 py-2.5 bg-[#091F5C] text-white text-[13px] font-semibold tracking-wide hover:bg-[#061745] transition-colors"
                >
                  {user.role === "admin"
                    ? "Concierge Desk"
                    : user.role === "landlord"
                    ? "Landlord Portal"
                    : "Tenant Portal"}
                </Link>
                <div className="flex items-center space-x-2 pl-3 border-l border-[#E5EBF5]">
                  <span className="text-xs text-[#4A5B78] font-medium max-w-[120px] truncate">
                    {user.name}
                  </span>
                  <button
                    onClick={handleLogout}
                    data-testid="nav-logout-btn"
                    title="Log Out"
                    className="p-2 text-[#4A5B78] hover:text-[#091F5C] transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login-btn"
                  className="px-4 py-2 text-[13px] font-medium text-[#091F5C] hover:text-[#334DAF] transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register-btn"
                  className="px-5 py-2.5 bg-[#091F5C] text-white text-[13px] font-semibold tracking-wide hover:bg-[#061745] transition-colors"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
              className="p-2 text-[#091F5C]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-[#E5EBF5] px-6 py-6 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-${link.to.replace("/", "")}`}
              className="block py-3 text-[15px] font-medium text-[#091F5C] border-b border-[#F1F5F9]"
            >
              {link.label}
            </Link>
          ))}
          <div className="pt-4 space-y-2">
            {isAuthenticated ? (
              <>
                <Link
                  to={getDashboardPath()}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-dashboard"
                  className="block w-full text-center py-3 bg-[#091F5C] text-white font-semibold text-sm tracking-wide"
                >
                  Open Portal
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  data-testid="mobile-nav-logout"
                  className="block w-full text-center py-3 border border-[#091F5C] text-[#091F5C] font-medium text-sm"
                >
                  Log Out
                </button>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-login"
                  className="text-center py-3 border border-[#091F5C] text-[#091F5C] font-medium text-sm"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-register"
                  className="text-center py-3 bg-[#091F5C] text-white font-semibold text-sm"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
